/**
 * /ndt-inspection/ai/:id — one AI digest on its own page
 * (GET /ndt-inspection/ai/digests/:id, fieldiq-api#1177 / #1178). The back
 * arrow returns to the register with its filter.
 *
 * Panels stack full width: the deterministic figures first (the API's, in
 * full), then the AI draft, labelled "Draft — needs review" with Approve and
 * Dismiss while the run is pending. A dose review's canonical mSv / µSv/h
 * print in the tenant's unit (lib/ndt-ai.ts ndtDoseText), from the NDT meta. A run with no draft still shows its
 * figures and says the draft is unavailable, with the API's reason.
 */
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useNdtAiDigest } from '../../hooks/useNdtAi';
import { useNdtCatalog } from '../../hooks/useNdtInspection';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { formatInstant } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canUseNdtAi, ndtAiAgentLabel, ndtAiOutcomeSkin, ndtAiScopeText } from '../../lib/ndt-ai';
import { useBackToList } from '../downhole/recordPage';
import { AiDraftPanel, DigestFigures } from './NdtAiBits';
import { NdtBadge, NdtLoading, isNotFound, ndtDay } from './NdtBits';

const LIST = '/ndt-inspection/ai';

export default function AiDigestDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const back = useBackToList(LIST);
  const { user } = useAuth();
  const allowed = canUseNdtAi(user?.role);
  const tz = useTenantTimeZone();
  const query = useNdtAiDigest(id, allowed);
  // The tenant's unit system, from the NDT meta (the same resolution Dosimetry's doses arrive in).
  const unitSystem = useNdtCatalog().data?.meta.unitSystem ?? null;
  const digest = query.data ?? null;
  const failure = queryLoadFailure(query);
  const notFound = !digest && !query.isLoading && isNotFound(queryFailureError(query));
  const ticketId = digest && typeof digest.triggerData?.ticketId === 'string' ? (digest.triggerData.ticketId as string) : null;

  return (
    <div data-testid="ndt-ai-digest-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="NDT AI digest"
        title={digest ? ndtAiAgentLabel(digest.agent) : query.isLoading && allowed ? 'Loading…' : 'AI digest'}
        onBack={back}
        backLabel="Back to AI digests"
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4" data-testid="ndt-ai-digest-detail">
          {!allowed ? (
            <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-6 text-sm text-fieldiq-text" data-testid="ndt-ai-not-allowed">
              AI digests are for admins, office staff and supervisors.
            </p>
          ) : !digest && query.isLoading ? (
            <NdtLoading label="Loading the digest" />
          ) : notFound ? (
            <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-6 text-sm text-fieldiq-text" data-testid="ndt-ai-not-found">
              This digest was not found.
            </p>
          ) : !digest ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? undefined} subject="this digest" />
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <NdtBadge skin={ndtAiOutcomeSkin(digest.outcome)} value={digest.outcome} />
                <span className="text-fieldiq-text">{ndtAiScopeText(digest.agent, digest.triggerData, ndtDay)}</span>
                <span className="text-fieldiq-muted">Run {formatInstant(digest.createdAt, tz) ?? '—'}</span>
                {digest.reviewedAt && <span className="text-fieldiq-muted">· Reviewed {formatInstant(digest.reviewedAt, tz) ?? '—'}</span>}
                {ticketId && (
                  <Link to={`/ndt-inspection/tickets/${ticketId}`} className="font-bold text-fieldiq-text underline" data-testid="ndt-ai-ticket-link">
                    Open the ticket
                  </Link>
                )}
              </div>
              {digest.outcome === 'superseded' && (
                <p role="status" className="text-xs text-fieldiq-muted">A newer run of this digest replaced this one, so it can no longer be approved.</p>
              )}
              <DigestFigures agent={digest.agent} data={digest.data} unitSystem={unitSystem} />
              <AiDraftPanel runId={digest.agentRunId} outcome={digest.outcome} draft={digest.draft} aiError={digest.aiError} canReview={allowed} />
            </>
          )}
        </div>
      </section>
    </div>
  );
}
